import {
  startOfWeek as _startOfWeek,
  addDays,
  addWeeks,
  compareAsc,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isSameWeek,
  startOfMonth,
} from 'date-fns';
import { parseTimeString } from './parseTimeString';

const getLastDays =
  (days: number) =>
  (date: Date): Date[] =>
    Array.from(Array(days).keys())
      .map((i) => {
        const day = new Date(date);
        day.setDate(day.getDate() - i);
        return day;
      })
      .reverse();

export const getLast7Days = getLastDays(7);

const getLastWeeks =
  (weeks: number) =>
  (date: Date): Date[] =>
    Array.from(Array(weeks).keys())
      .map((i) => {
        const day = new Date(date);
        day.setDate(day.getDate() - i * 7);
        return day;
      })
      .reverse();

export const getLast7Weeks = getLastWeeks(7);

const getLastMonths = (months: number) => (date: Date) =>
  Array.from(Array(months).keys())
    .map((i) => {
      const day = new Date(date);
      day.setMonth(day.getMonth() - i);
      return day;
    })
    .reverse();

export const getLast7Months = getLastMonths(7);

export const sameTimeOfDay = (date1: Date, date2: Date) => {
  const time1 = date1.getHours() + ':' + date1.getMinutes();
  const time2 = date2.getHours() + ':' + date2.getMinutes();

  return time1 === time2;
};

export const getDateDifference = (date1: Date, date2: Date): number =>
  Math.abs((date1 as any) - (date2 as any));

export const sortByDateField =
  <T extends Record<K, Date>, K extends PropertyKey>(fieldName: K) =>
  (a: T, b: T) =>
    compareAsc(a[fieldName], b[fieldName]);

export const sortByStartDate = sortByDateField('start');

export const dateToWeekString = (date: Date) => format(date, 'yyyy-ww');

export const formatDateString = (date_string: string, format_string: string) =>
  format(new Date(date_string), format_string);

export const toDateString = (date: Date) => format(date, 'yyyy-MM-dd');

export const startOfWeek: typeof _startOfWeek = (date, options) =>
  _startOfWeek(date, { weekStartsOn: 1, ...options });

export const mutateDateTimeFromTimeString = (date: Date, time: string) => {
  try {
    const [hour_str, minute_str, second_str] = time.split(':');

    const hours = removeLeadingZerosAndParseInt(hour_str);
    const minutes = removeLeadingZerosAndParseInt(minute_str);

    if (typeof hours !== 'number') return;
    if (typeof minutes !== 'number') return;

    date.setHours(hours, minutes);

    if (second_str) {
      const seconds = removeLeadingZerosAndParseInt(second_str);

      if (typeof seconds !== 'number') return;
      date.setSeconds(seconds);
    }
  } catch (ex) {
    console.log(ex);

    return ex;
  }

  return;
};

export function removeLeadingZerosAndParseInt(input: string): number {
  // Remove leading zeros using regular expression
  const stringWithoutZeros = input.replace(/^0+/, '');

  // Parse the string into an integer
  const parsedInt = parseInt(
    stringWithoutZeros.length ? stringWithoutZeros : '0',
    10
  );

  return parsedInt;
}

export const startOfWeekMonday = (date: Date) =>
  startOfWeek(date, { weekStartsOn: 1 });

export const endOfWeekSunday = (date: Date) =>
  endOfWeek(date, { weekStartsOn: 1 });

export const getWeekText = (date: Date) => {
  const today = new Date();

  if (isSameWeek(date, today, { weekStartsOn: 1 })) {
    return 'This week';
  }

  if (isSameWeek(date, addWeeks(today, 1), { weekStartsOn: 1 })) {
    return 'Next week';
  }

  if (isSameWeek(date, addWeeks(today, -1), { weekStartsOn: 1 })) {
    return 'Last week';
  }

  return `${format(startOfWeekMonday(date), 'MMM d')} to ${format(
    endOfWeekSunday(date),
    'MMM d'
  )}`;
};

export function isValidTimeFormat(timeString?: string): timeString is string {
  if (!timeString) return false;
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/; // Regex pattern for "hh:mm"
  return timeRegex.test(timeString);
}

export function formatTime(timeString: string): string {
  const [hours, minutes] = parseTimeString(timeString).map(Number);

  if (!isValidTimeFormat(timeString)) {
    throw new Error(
      'Invalid time format. Please provide a valid time string in the format "hh:mm".'
    );
  }

  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);

  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export const convertToNon24HourFormat = (hour_24_format: string) => {
  const hour = parseInt(hour_24_format);

  if (hour === 0 || hour === 24) {
    return '12';
  } else if (hour < 12) {
    return hour.toString().padStart(2, '0');
  } else if (hour === 12) {
    return '12';
  } else {
    return (hour - 12).toString().padStart(2, '0');
  }
};

/** Must take a valid time string */
export const getMinuteValueFromTimeString = (time: string) => {
  return parseInt(time.split(':')[1]);
};

export const getWeeksOfMonth = (date: Date) => {
  const weeks = eachWeekOfInterval(
    {
      start: startOfMonth(date),
      end: endOfMonth(date),
    },
    { weekStartsOn: 1 }
  );

  return weeks.map((week) => {
    const days = eachDayOfInterval({
      start: startOfWeekMonday(week),
      end: endOfWeekSunday(week),
    });

    return days.map((day) => (isSameMonth(day, date) ? day : undefined));
  });
};

export const getPaddedWeeksOfMonth = (date: Date) => {
  const current_month_weeks = getWeeksOfMonth(new Date(date));

  /** Middle weeks, these won't need padding */
  const middle_weeks = current_month_weeks.slice(1, -1);

  /** Strip undefined dates */
  let first_week = current_month_weeks[0].filter(Boolean);

  /** If the first week is not a full week, we need to pad it */
  if (first_week.length < 7) {
    /** First week is short, add padding */
    const first_day = first_week[0] as Date;

    /** Get the padding days */
    const padding_days = Array.from(Array(7 - first_week.length), (_, idx) =>
      addDays(first_day, -(idx + 1))
    ).reverse();

    /** Add padding days to first week */
    first_week = [...padding_days, ...first_week];
  }

  /** Strip undefined dates */
  let last_week =
    current_month_weeks[current_month_weeks.length - 1].filter(Boolean);

  /** If the last week is not a full week, we need to pad it */
  if (last_week.length < 7) {
    /** Last week is short, add padding */
    const last_day = last_week[last_week.length - 1] as Date;

    /** Get the padding days */
    const padding_days = Array.from(Array(7 - last_week.length), (_, idx) =>
      addDays(last_day, idx + 1)
    ).reverse();

    /** Add padding days to last week */
    last_week = [...last_week, ...padding_days];
  }

  return [first_week, ...middle_weeks, last_week];
};

export const getDateString = (date: Date) => format(date, 'yyyy-MM-dd');
export const getTimeString = (date: Date) => format(date, 'hh:mm');

export const past = (date: Date) => date < new Date();

export const pastWithinLast20Seconds = (date: Date) => {
  if (!past(date)) return false;

  const timeDifference = new Date().getTime() - date.getTime();
  const secondsDifference = Math.abs(timeDifference) / 1000;

  return secondsDifference < 30;
};

export const isTimePast = (time_text: string) => {
  // Split the provided time text into hours and minutes
  const [hours, minutes] = time_text.split(':').map(Number);

  // Get the current date and time
  const currentDate = new Date();
  const currentHour = currentDate.getHours();
  const currentMinute = currentDate.getMinutes();

  // Compare the provided time with the current time
  if (
    hours < currentHour ||
    (hours === currentHour && minutes <= currentMinute)
  ) {
    return true; // Time has passed
  } else {
    return false; // Time has not passed
  }
};

export function normalizeDateString(dateString: string): string {
  const [year, month, day] = dateString.split('-');

  if (year.length !== 4) {
    // If this happens, then the date is likely in the format dd-mm-yyyy.
    // JS dates don't handle this, well, so we should flip it.
    return [day, month, year].join('-');
  }

  // String looks fine, just return it
  return dateString;
}
